import React, { useState, useEffect, useContext } from 'react'
import { AllEvents } from 'libs/web-common/src/events'
import {
    ExtendedInterknowlogyData,
    getPreloadedElectionDefinition,
    loadElectionDefinition,
    transformAreaData,
} from '../data'
import {
    StyledBackgroundBar,
    StyledBarContent,
    StyledBarsContainer,
    StyledCenterLabel,
    StyledCenterLine,
    StyledContainer,
    StyledLoadingPlaceholder,
    StyledPartyBar,
    StyledPartyLabel,
    StyledPartyLabelSeatCount,
    StyledPredictedSeats,
    StyledSeatCount,
    StyledSeatCountContainer,
    StyledSeatCountErrorContainer,
    StyledSeatCountErrorText,
    StyledWonSeats,
    StyledWrapper,
} from './HeadToHeadSeatCountWidget.styled'
import { ConfigurationContext, ElectionDefinition } from '@news-mono/web-common'

interface PartyResults {
    wonSeats: number
    predictedSeats: number
    partyName: string
}

interface HeadToHeadSeatCountData {
    coalition: PartyResults
    labor: PartyResults
    seatsToWin: number
    seatsTotal: number
}

const getInitialData = (
    seatsToWin: number,
    seatsTotal: number,
): HeadToHeadSeatCountData => {
    return {
        coalition: {
            wonSeats: 0,
            predictedSeats: 0,
            partyName: 'Coalition',
        },
        labor: {
            wonSeats: 0,
            predictedSeats: 0,
            partyName: 'Labor',
        },
        seatsToWin: seatsToWin,
        seatsTotal: seatsTotal,
    }
}

const transformToSeatCountData = (
    data: ExtendedInterknowlogyData,
    seatsToWin: number,
    seatsTotal: number,
): HeadToHeadSeatCountData => {
    const seatCountData = getInitialData(seatsToWin, seatsTotal)
    data.areas.forEach((area) => {
        area.parties.forEach((party) => {
            // add to labor seat count based on partyCode ALP
            if (party.partyCode === 'ALP') {
                seatCountData.labor.wonSeats += party.seatsWon
                seatCountData.labor.predictedSeats += party.seatsAhead
            }
            // add to coalition seat count based on combinedCode COL
            if (party.combinedCode === 'COL') {
                seatCountData.coalition.wonSeats += party.seatsWon
                seatCountData.coalition.predictedSeats += party.seatsAhead
            }
        })
    })

    return seatCountData
}

export interface HeadToHeadSeatCountWidgetProps {
    electionDefinition: ElectionDefinition
    onEvent: (event: AllEvents) => void
}

/**
 * Amount of total seats from s3 endpoint data needs to match election definition seatsTotal
 */
const HeadToHeadSeatCountWidget = ({
    electionDefinition,
    onEvent,
}: HeadToHeadSeatCountWidgetProps) => {
    const config = useContext(ConfigurationContext)
    const preloadedDefinition =
        getPreloadedElectionDefinition(electionDefinition)
    const initialData =
        preloadedDefinition &&
        preloadedDefinition.electionData.data &&
        preloadedDefinition.electionData.config
            ? transformToSeatCountData(
                  transformAreaData(preloadedDefinition.electionData.data),
                  preloadedDefinition.electionData.config.seatsToWin,
                  preloadedDefinition.electionData.config.seatsTotal,
              )
            : undefined
    const [seatCountData, setSeatCountData] = useState<
        HeadToHeadSeatCountData | undefined
    >(initialData)
    const [failedToLoad, setFailedToLoad] = useState<boolean>(
        preloadedDefinition && !preloadedDefinition.electionData.data,
    )

    useEffect(() => {
        const load = async () => {
            const fetchedDefinition = await loadElectionDefinition(
                electionDefinition,
                config,
            )

            if (
                fetchedDefinition.electionData.data &&
                fetchedDefinition.electionData.config
            ) {
                const transformedData = transformToSeatCountData(
                    transformAreaData(fetchedDefinition.electionData.data),
                    fetchedDefinition.electionData.config.seatsToWin,
                    fetchedDefinition.electionData.config.seatsTotal,
                )
                setSeatCountData(transformedData)
                setFailedToLoad(false)
            } else {
                console.warn(
                    'No data was found in the loaded election definition for HeadToHeadSeatCountWidget!',
                )
                setSeatCountData(undefined)
                setFailedToLoad(true)
            }
        }

        try {
            load()
        } catch (error) {
            console.error(
                { error },
                `Failed to load election definition for HeadToHeadSeatCountWidget!`,
            )
            setFailedToLoad(true)
        }
    }, [config, electionDefinition])

    // Error State (data has loaded, but there is no data)
    if (failedToLoad) {
        return (
            <StyledSeatCountErrorContainer>
                <StyledSeatCountErrorText isBold={true}>
                    Error
                </StyledSeatCountErrorText>
                <StyledSeatCountErrorText>
                    Refresh the page or try again later.
                </StyledSeatCountErrorText>
            </StyledSeatCountErrorContainer>
        )
    }

    // Loading State
    if (!electionDefinition.loaded || !seatCountData) {
        return <StyledLoadingPlaceholder />
    }

    const coalitionTotal =
        seatCountData.coalition.wonSeats +
        seatCountData.coalition.predictedSeats
    const laborTotal =
        seatCountData.labor.wonSeats + seatCountData.labor.predictedSeats
    const totalSeats = seatCountData.seatsTotal

    // Width as percentage
    const coalitionWidth = (coalitionTotal / totalSeats) * 100
    const laborWidth = (laborTotal / totalSeats) * 100
    const coalitionSeatsWonWidth =
        (seatCountData.coalition.wonSeats / coalitionTotal) * 100
    const laborSeatsWonWidth = (seatCountData.labor.wonSeats / laborTotal) * 100
    const coalitionSeatsPredictedWidth =
        (seatCountData.coalition.predictedSeats / coalitionTotal) * 100
    const laborSeatsPredictedWidth =
        (seatCountData.labor.predictedSeats / laborTotal) * 100

    return (
        <StyledContainer>
            <StyledWrapper>
                {/* Coalition Seat Count */}
                <StyledSeatCountContainer>
                    <StyledSeatCount>
                        {seatCountData.coalition.wonSeats}
                    </StyledSeatCount>
                </StyledSeatCountContainer>

                <StyledBarsContainer>
                    {/* Center Line */}
                    <StyledCenterLine>
                        <StyledCenterLabel>
                            {seatCountData.seatsToWin} to win
                        </StyledCenterLabel>
                    </StyledCenterLine>

                    <StyledBackgroundBar />

                    {/* Coalition Bar */}
                    <StyledPartyBar width={coalitionWidth}>
                        <StyledPartyLabel>
                            <StyledPartyLabelSeatCount>
                                {seatCountData.coalition.wonSeats}
                            </StyledPartyLabelSeatCount>{' '}
                            Coalition
                        </StyledPartyLabel>
                        <StyledBarContent>
                            <StyledWonSeats
                                partyType="coalition"
                                width={coalitionSeatsWonWidth}
                            />
                            <StyledPredictedSeats
                                partyType="coalition"
                                width={coalitionSeatsPredictedWidth}
                            />
                        </StyledBarContent>
                    </StyledPartyBar>

                    {/* Labor Bar */}
                    <StyledPartyBar width={laborWidth} isRight>
                        <StyledPartyLabel isRight>
                            Labor{' '}
                            <StyledPartyLabelSeatCount>
                                {seatCountData.labor.wonSeats}
                            </StyledPartyLabelSeatCount>
                        </StyledPartyLabel>
                        <StyledBarContent>
                            <StyledPredictedSeats
                                partyType="labor"
                                width={laborSeatsPredictedWidth}
                            />
                            <StyledWonSeats
                                partyType="labor"
                                width={laborSeatsWonWidth}
                            />
                        </StyledBarContent>
                    </StyledPartyBar>
                </StyledBarsContainer>

                {/* Labor Seat Count */}
                <StyledSeatCountContainer isRight>
                    <StyledSeatCount isRight>
                        {seatCountData.labor.wonSeats}
                    </StyledSeatCount>
                </StyledSeatCountContainer>
            </StyledWrapper>
        </StyledContainer>
    )
}

export default HeadToHeadSeatCountWidget
